import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Edit2, XCircle, Printer, Download, MessageCircle, Mail,
  CreditCard, ShieldCheck, Plus, X, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INVOICE_STATUS_META, PAYMENT_METHODS, inr } from "./lib";
import { downloadInvoicePdf } from "@/lib/pdf";
import WorkflowStrip from "@/components/WorkflowStrip";

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { garage } = useAuth();
  const [inv, setInv] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [gpOpen, setGpOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/billing/invoices/${id}`);
      setInv(r.data);
      const a = await api.get(`/billing/invoices/${id}/audit`);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load invoice");
      nav("/invoices");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    try {
      await api.post(`/billing/invoices/${id}/issue`);
      toast.success("Invoice issued");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not issue"); }
  };

  const printPdf = async () => {
    if (!inv) return;
    try {
      await downloadInvoicePdf(inv, garage);
      await api.post(`/billing/invoices/${id}/share`, { channel: "download" });
    } catch (e) {
      toast.error("Could not build PDF");
    }
  };

  if (loading || !inv) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading invoice…</div>;

  const st = INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.draft;
  const isDraft = inv.status === "draft";
  const canPay = !["draft", "cancelled", "closed", "gate_pass_issued"].includes(inv.status);
  const canGatePass = ["paid", "partially_paid", "payment_pending", "issued"].includes(inv.status);
  const activeGp = (inv.gate_passes || []).find((g) => g.status !== "cancelled");

  return (
    <div className="space-y-4" data-testid="invoice-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/invoices" data-testid="invoice-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight font-mono">{inv.number}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${st.chip}`}
                    data-testid="invoice-detail-status">{st.label}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                inv.invoice_type === "gst" ? "bg-[#457b9d]/15 text-[#1d3557]" : "bg-[#a8dadc]/25 text-[#457b9d]"
              }`}>{inv.invoice_type === "gst" ? "GST" : "NON-GST"}</span>
            </div>
            <div className="text-[12.5px] text-[#667085] mt-0.5">
              {inv.customer_snapshot?.name} · {inv.vehicle_snapshot?.reg_number || "—"}
              {inv.job_card_number && <> · JC <Link to={`/job-cards/${inv.job_card_id}`} className="text-[#457b9d] hover:text-[#e63946] font-mono">{inv.job_card_number}</Link></>}
              · {new Date(inv.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft && (
            <>
              <Link to={`/invoices/${id}/edit`}
                    data-testid="invoice-edit"
                    className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
                <Edit2 size={13} /> Edit
              </Link>
              <button onClick={issue} data-testid="invoice-issue"
                      className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
                <FileText size={13} /> Issue
              </button>
            </>
          )}
          {canPay && (
            <button onClick={() => setPayOpen(true)} data-testid="invoice-record-payment"
                    className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <CreditCard size={13} /> Record Payment
            </button>
          )}
          {canGatePass && !activeGp && (
            <button onClick={() => setGpOpen(true)} data-testid="invoice-create-gate-pass"
                    className="h-10 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <ShieldCheck size={13} /> Create Gate Pass
            </button>
          )}
          {!isDraft && (
            <>
              <button onClick={printPdf} data-testid="invoice-print"
                      className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
                <Download size={13} /> PDF
              </button>
              <button onClick={() => setShareOpen(true)} data-testid="invoice-share"
                      className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
                <MessageCircle size={13} /> Share
              </button>
            </>
          )}
          {!["cancelled", "closed"].includes(inv.status) && (
            <button onClick={() => setCancelOpen(true)} data-testid="invoice-cancel"
                    className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#e63946] hover:bg-[#e63946]/10 inline-flex items-center gap-1.5">
              <XCircle size={13} /> Cancel
            </button>
          )}
        </div>
      </header>

      {/* Guided workflow strip */}
      <WorkflowStrip
        currentStep="invoice"
        jobCard={inv.job_card_id ? { id: inv.job_card_id, number: inv.job_card_number } : null}
        invoice={{ id: inv.id, number: inv.number, status: inv.status }}
        gatePass={activeGp ? { id: activeGp.id, number: activeGp.number, status: activeGp.status } : null}
        nextAction={
          isDraft
            ? {
                label: "Issue Invoice",
                to: `/invoices/${inv.id}/edit`,
                testId: "invoice-workflow-issue-btn",
                hint: "This invoice is still in draft. Review the line items, then issue it to the customer.",
              }
            : activeGp
              ? null
              : canGatePass
                ? {
                    label: "Create Gate Pass",
                    to: `/gate-pass/new?invoice=${inv.id}`,
                    testId: "invoice-workflow-gate-pass-btn",
                    hint: (inv.balance || 0) > 0
                      ? `Balance ${inr(inv.balance)} pending — issue a gate pass with an owner override, or record payment first.`
                      : "Payment settled. Now generate a gate pass to release the vehicle.",
                  }
                : null
        }
        blockReason={
          activeGp
            ? `Gate pass ${activeGp.number} is ready. The workflow is complete.`
            : inv.status === "cancelled"
              ? "This invoice was cancelled. No further steps."
              : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="invoice-parties">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PartyBlock title="Customer" party={inv.customer_snapshot} />
              <VehicleBlock veh={inv.vehicle_snapshot} />
            </div>
          </section>

          <section className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F1FAEE]">
              <h3 className="text-[13px] font-bold text-[#1d3557]">Line items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="invoice-items-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-3 py-2.5">Item</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Rate</th>
                    <th className="px-3 py-2.5 text-right">Disc</th>
                    {inv.invoice_type === "gst" && <th className="px-3 py-2.5 text-right">GST</th>}
                    <th className="px-3 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(inv.items || []).map((it, i) => (
                    <tr key={i} className="border-b border-[#F1FAEE] last:border-0">
                      <td className="px-4 py-2.5 uppercase text-[10.5px] tracking-wider font-semibold text-[#457b9d]">
                        {it.kind}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-[#1d3557]">{it.name}</div>
                        {it.part_number && <div className="text-[10.5px] font-mono text-[#667085]">{it.part_number}</div>}
                        {it.description && <div className="text-[10.5px] text-[#667085]">{it.description}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{it.qty}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{inr(it.rate)}</td>
                      <td className="px-3 py-2.5 text-right text-[#667085]">
                        {it.discount_pct ? `${it.discount_pct}%` : (it.discount_amount ? inr(it.discount_amount) : "—")}
                      </td>
                      {inv.invoice_type === "gst" && <td className="px-3 py-2.5 text-right text-[#667085]">{it.gst_rate || 0}%</td>}
                      <td className="px-3 py-2.5 text-right font-mono font-bold">{inr(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {(inv.payments || []).length > 0 && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="invoice-payments-list">
              <div className="px-4 py-3 border-b border-[#F1FAEE]">
                <h3 className="text-[13px] font-bold text-[#1d3557]">Payments ({inv.payments.length})</h3>
              </div>
              <ul className="divide-y divide-[#F1FAEE]">
                {inv.payments.map((p) => (
                  <li key={p.id} className="px-4 py-2.5 flex items-center gap-3">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#12B76A]/12 text-[#0F9256]">
                      {p.method}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-mono font-bold text-[#0F9256]">{inr(p.amount)}</div>
                      <div className="text-[10.5px] text-[#667085]">
                        {new Date(p.payment_date).toLocaleString()} · {p.received_by}
                        {p.reference && <> · ref <span className="font-mono">{p.reference}</span></>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(inv.notes || inv.terms) && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-3">
              {inv.notes && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Notes</div>
                  <div className="text-[12.5px] text-[#1d3557]">{inv.notes}</div>
                </div>
              )}
              {inv.terms && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Terms</div>
                  <div className="text-[12.5px] text-[#1d3557] whitespace-pre-wrap">{inv.terms}</div>
                </div>
              )}
            </section>
          )}

          {audit.length > 0 && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="invoice-audit">
              <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Activity ({audit.length})</h3>
              <ul className="space-y-1.5 text-[11.5px] text-[#667085] max-h-56 overflow-y-auto">
                {audit.map((a) => (
                  <li key={a.id} className="flex gap-2">
                    <span className="font-mono text-[10.5px]">{new Date(a.at).toLocaleTimeString()}</span>
                    <span className="font-semibold text-[#1d3557]">{a.action}</span>
                    <span className="text-[#a8dadc]">·</span>
                    <span>{a.actor_name}</span>
                    {a.note && <span className="text-[#457b9d]">&ldquo;{a.note}&rdquo;</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside>
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="invoice-summary">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Summary</h3>
            <Row label="Parts" value={inr(inv.totals?.parts_subtotal)} muted />
            <Row label="Labour" value={inr(inv.totals?.labour_subtotal)} muted />
            {(inv.totals?.charges_subtotal || 0) > 0 && <Row label="Charges" value={inr(inv.totals?.charges_subtotal)} muted />}
            <div className="my-2 border-t border-[#F1FAEE]" />
            <Row label="Gross subtotal" value={inr(inv.totals?.gross_subtotal)} />
            {(inv.totals?.line_discount || 0) > 0 && <Row label="Line discount" value={`− ${inr(inv.totals?.line_discount)}`} muted />}
            {(inv.totals?.invoice_discount || 0) > 0 && <Row label="Invoice discount" value={`− ${inr(inv.totals?.invoice_discount)}`} muted />}
            <Row label="Taxable" value={inr(inv.totals?.taxable)} />
            {inv.invoice_type === "gst" && (inv.is_interstate ? (
              <Row label="IGST" value={inr(inv.totals?.igst)} muted />
            ) : (<>
              <Row label="CGST" value={inr(inv.totals?.cgst)} muted />
              <Row label="SGST" value={inr(inv.totals?.sgst)} muted />
            </>))}
            <div className="my-2 border-t border-[#F1FAEE]" />
            <Row label="Invoice total" value={inr(inv.totals?.invoice_total)} big />
            {(inv.advance_amount || 0) > 0 && <Row label="Advance received" value={`− ${inr(inv.advance_amount)}`} muted />}
            <Row label="Paid" value={inr(inv.paid_amount)} muted />
            <div className="my-2 border-t border-[#F1FAEE]" />
            <Row label="Balance due" value={inr(inv.balance)} big
                 tone={inv.balance <= 0.01 ? "green" : "red"} />
            {activeGp && (
              <div className="mt-3 rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px] text-[#1d3557]">
                <div className="font-bold text-[10.5px] uppercase tracking-wider text-[#457b9d]">Linked gate pass</div>
                <Link to={`/gate-pass/${activeGp.id}`}
                      data-testid="invoice-linked-gp"
                      className="font-mono font-semibold text-[#1d3557] hover:text-[#e63946]">
                  {activeGp.number}
                </Link>
                <span className="ml-2 text-[10.5px] uppercase tracking-wider text-[#457b9d]">{activeGp.status}</span>
              </div>
            )}
          </section>
        </aside>
      </div>

      {payOpen && (
        <PaymentDialog invoice={inv} onClose={() => setPayOpen(false)}
                       onSaved={() => { setPayOpen(false); load(); }} />
      )}
      {gpOpen && (
        <CreateGatePassRedirect inv={inv} onClose={() => setGpOpen(false)} />
      )}
      {shareOpen && <ShareDialog inv={inv} onClose={() => setShareOpen(false)} />}
      {cancelOpen && (
        <CancelDialog inv={inv} onClose={() => setCancelOpen(false)}
                      onCancelled={() => { setCancelOpen(false); load(); }} />
      )}
    </div>
  );
}

function Row({ label, value, muted, big, tone }) {
  const cls = big ? "text-[15px] font-bold" : "text-[12.5px]";
  const toneCls = tone === "green" ? "text-[#0F9256]" : tone === "red" ? "text-[#e63946]" : "";
  return (
    <div className={`flex items-center justify-between py-1 ${cls}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"} ${toneCls}`}>{value}</span>
    </div>
  );
}

function PartyBlock({ title, party }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{title}</div>
      <div className="text-[14px] font-bold text-[#1d3557]">{party?.name || "—"}</div>
      <div className="text-[12px] text-[#667085] font-mono">{party?.phone || ""}</div>
      {party?.email && <div className="text-[12px] text-[#667085]">{party.email}</div>}
      {party?.address && <div className="text-[11.5px] text-[#667085] mt-1">{party.address}, {party.city || ""}</div>}
      {party?.gstin && <div className="text-[11px] font-mono text-[#457b9d] mt-1">GST: {party.gstin}</div>}
    </div>
  );
}

function VehicleBlock({ veh }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Vehicle</div>
      <div className="text-[14px] font-bold font-mono text-[#1d3557]">{veh?.reg_number || "—"}</div>
      <div className="text-[12px] text-[#667085]">{[veh?.make, veh?.model, veh?.variant].filter(Boolean).join(" ") || "—"}</div>
      <div className="text-[11.5px] text-[#667085] flex gap-2">
        {veh?.fuel_type && <span>{veh.fuel_type}</span>}
        {veh?.odometer && <span>· {veh.odometer} km</span>}
      </div>
    </div>
  );
}

function PaymentDialog({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(invoice.balance);
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!(Number(amount) > 0)) { setError("Amount must be positive"); return; }
    setSaving(true); setError(null);
    try {
      await api.post(`/billing/invoices/${invoice.id}/payments`, {
        amount: Number(amount), method, reference, notes,
      });
      toast.success(`Received ${inr(amount)}`);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="payment-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="payment-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#12B76A]/10">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">Record Payment</h2>
            <p className="text-[11px] text-[#667085]">Balance due: <span className="font-mono font-bold">{inr(invoice.balance)}</span></p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white grid place-items-center text-[#667085]"
                  data-testid="payment-dialog-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Amount *</span>
            <input type="number" min={0} value={amount}
                   onChange={(e) => setAmount(Number(e.target.value) || 0)}
                   data-testid="payment-amount"
                   className="mt-1 h-11 px-3 rounded-md border border-[#E4E7EC] text-[16px] font-mono tabular-nums w-full font-bold" />
          </label>
          <div>
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Method</span>
            <div className="mt-1 grid grid-cols-4 gap-1">
              {PAYMENT_METHODS.slice(0, 5).map((m) => (
                <button key={m.key} type="button" onClick={() => setMethod(m.key)}
                        data-testid={`payment-method-${m.key}`}
                        className={`h-9 rounded-md text-[11px] font-semibold uppercase tracking-wider ${
                          method === m.key ? "bg-[#12B76A] text-white" : "border border-[#E4E7EC] text-[#667085]"
                        }`}>{m.label}</button>
              ))}
            </div>
          </div>
          {method !== "cash" && (
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Reference</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)}
                     data-testid="payment-reference"
                     placeholder="Transaction / UTR / cheque #"
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </label>
          )}
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="payment-error">{error}</div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
                  data-testid="payment-submit"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Recording…" : "Record"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function CreateGatePassRedirect({ inv, onClose }) {
  const nav = useNavigate();
  useEffect(() => {
    nav(`/gate-pass/new?invoice=${inv.id}`);
     
  }, []);
  return null;
}

function ShareDialog({ inv, onClose }) {
  const [msg] = useState(
    `Dear ${inv.customer_snapshot?.name || "Customer"}, please find Invoice ${inv.number} for ${inv.vehicle_snapshot?.reg_number || "your vehicle"}. Invoice amount: ${inr(inv.totals?.invoice_total)}. Amount received: ${inr(inv.paid_amount)}. Balance: ${inr(inv.balance)}.`
  );
  const shareVia = async (channel) => {
    if (channel === "whatsapp") {
      const phone = (inv.customer_snapshot?.whatsapp || inv.customer_snapshot?.phone || "").replace(/[^0-9]/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } else if (channel === "email") {
      window.location.href = `mailto:${inv.customer_snapshot?.email || ""}?subject=${encodeURIComponent(`Invoice ${inv.number}`)}&body=${encodeURIComponent(msg)}`;
    }
    await api.post(`/billing/invoices/${inv.id}/share`, { channel });
    toast.success(`Shared via ${channel}`);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="share-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="share-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] bg-[#F7F9FC]">
          <h2 className="text-[16px] font-bold text-[#1d3557]">Share Invoice</h2>
        </header>
        <div className="p-5 space-y-3">
          <p className="text-[12.5px] text-[#1d3557]">{msg}</p>
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={() => shareVia("email")} data-testid="share-email"
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-white inline-flex items-center gap-1.5">
            <Mail size={13} /> Email
          </button>
          <button onClick={() => shareVia("whatsapp")} data-testid="share-whatsapp"
                  className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
            <MessageCircle size={13} /> WhatsApp
          </button>
        </footer>
      </div>
    </div>
  );
}

function CancelDialog({ inv, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!reason.trim()) { setError("Reason is required"); return; }
    setSaving(true); setError(null);
    try {
      await api.post(`/billing/invoices/${inv.id}/cancel`, { reason });
      toast.success("Invoice cancelled");
      onCancelled();
    } catch (e) { setError(e.response?.data?.detail || "Could not cancel"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="cancel-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="cancel-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] bg-[#e63946]/10">
          <h2 className="text-[16px] font-bold text-[#e63946]">Cancel Invoice {inv.number}</h2>
          <p className="text-[11px] text-[#667085] mt-0.5">The number will remain reserved and cannot be re-used.</p>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Reason *</span>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="cancel-reason"
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Keep invoice
          </button>
          <button onClick={submit} disabled={saving}
                  data-testid="cancel-confirm"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50">
            {saving ? "Cancelling…" : "Confirm cancel"}
          </button>
        </footer>
      </div>
    </div>
  );
}
